import { useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import { useRouter } from 'next/router';
import { useProductStore } from '@portal/state/store/pdpStore';
import { resolveInitialPdpState } from '@portal/shared/studio/ProductDetailPage/resolveInitialPdpState';

type PDPType = 'productView' | 'bundleView' | 'otpView' | null;
const normalizePdpType = (t: unknown): PDPType =>
  t === 'productView' || t === 'bundleView' || t === 'otpView' ? (t as PDPType) : null;

export const useResetPdpOnRouteChange = (
  productKey: string | null,
  variants: any[] | null,
  store: string,
  pdpTypeRaw?: string | null
) => {
  const router = useRouter();
  const { resetAll, setFlavor, setVariants, setPdpType } = useProductStore();

  // read bundle readiness from the store
  const bundleVariant = useProductStore((s) => s.bundleVariant);
  const bundleData = useProductStore((s) => s.bundleData);
  const bundleReady = !!bundleVariant && !!bundleData;

  const variantsReady = useMemo(() => !!variants?.length, [variants?.length]);
  const pdpType = normalizePdpType(pdpTypeRaw);
  const isBundle = pdpType === 'bundleView';

  const identityKey = useMemo(
    () => (productKey ? `${productKey}|type:${pdpType ?? 'null'}|store:${store}` : ''),
    [productKey, pdpType, store]
  );

  const resetOnceRef = useRef<string | null>(null);
  // remember the last identity we hydrated for this mounted component
  const lastGuardRef = useRef<string | null>(null);
  const wroteFlavorRef = useRef<string | null>(null);
  // pre-reset: clears stale PDP state before any render uses it so theres NO flashing between PDPs
  useLayoutEffect(() => {
    if (!identityKey || resetOnceRef.current === identityKey) return;

    resetAll(); // clears selectedVariantId, flavor, etc.
    if (useProductStore.getState().pdpType !== pdpType) setPdpType(pdpType);
    resetOnceRef.current = identityKey;
  }, [identityKey, isBundle, pdpType, resetAll, setPdpType, setVariants]);

  useEffect(() => {
    if (!router.isReady || !productKey || !store) return;
    // wait for data
    if (!isBundle && !variantsReady) return;

    // Identity key: changes when product/type/store change (NOT on query-only changes)
    const guardKey = identityKey;

    // Early exit when bundle is already hydrated
    // Bundle PDPs: we don’t get per-variant data in the page payload. We derive it by calling useShopListBundleCollectionQuery(...),
    // which runs after mount (and depends on currentStore + the collection handle). So bundle data naturally “comes later” so have to
    // be careful NOT to run this AGAIN once the data comes back or it will CLEAR STATE and undo all that good work :)
    if (isBundle && bundleReady) {
      // Ensure the UI takes the bundle branch immediately
      if (useProductStore.getState().pdpType !== 'bundleView') setPdpType('bundleView');

      // Optional: only set flavor if URL has it; otherwise let ContentPage decide
      const urlFlavor =
        typeof router.query.flavor === 'string' && router.query.flavor.trim()
          ? (router.query.flavor as string)
          : null;
      if (urlFlavor) setFlavor(urlFlavor);

      lastGuardRef.current = guardKey; // mark identity handled so this effect won’t run again
      return;
    }

    if (lastGuardRef.current === guardKey) return;

    // Only call resetAll here if the pre-reset DIDN’T run otherwise this will wipe out
    // any state setters that already ran
    if (resetOnceRef.current !== identityKey) {
      resetAll();
      if (useProductStore.getState().pdpType !== pdpType) setPdpType(pdpType);
    }

    setVariants?.(isBundle ? null : variants || null);

    // inside your hydrate effect, replace the noisy branch with:
    const flavorParam =
      typeof router.query.flavor === 'string' && router.query.flavor.trim()
        ? (router.query.flavor as string)
        : '';

    const qtyParamRaw =
      (typeof router.query.quantity === 'string' && router.query.quantity) ||
      (typeof router.query.qty === 'string' && router.query.qty) ||
      '';

    if (!isBundle) {
      const init = resolveInitialPdpState({
        // snapshot only the keys you need; don't pass the whole query
        urlQuery: {
          flavor: flavorParam,
          quantity: qtyParamRaw,
          qty: qtyParamRaw,
          plan: router.query.plan
        },
        variants: variants || [],
        store,
        pdpType
      });

      // make a single, idempotent patch
      const s = useProductStore.getState();
      const patch: Partial<ReturnType<typeof useProductStore.getState>> = {};

      if (init.flavor && s.flavor !== init.flavor) patch.flavor = init.flavor;
      if (s.isOtpProduct !== init.isOtpProduct) patch.isOtpProduct = init.isOtpProduct;
      if (init.selectedVariantId && s.selectedVariantId !== init.selectedVariantId) {
        patch.selectedVariantId = init.selectedVariantId;
      }
      if (s.quantity !== init.quantity) patch.quantity = init.quantity;

      if (Object.keys(patch).length) {
        useProductStore.setState(patch, false);
      }

      // write flavor to URL only if missing and not mid-navigation
      if (!flavorParam && init.flavor && wroteFlavorRef.current !== identityKey) {
        const nextQuery = { ...router.query, flavor: init.flavor };
        // avoid no-op replace
        const same = typeof router.query.flavor === 'string' && router.query.flavor === init.flavor;
        if (!same) {
          router.replace({ pathname: router.pathname, query: nextQuery }, undefined, {
            shallow: true,
            scroll: false
          });
        }
      }
    } else {
      // bundle (no per-variant yet): minimal, batched, idempotent write
      const s = useProductStore.getState();
      const urlFlavor =
        typeof router.query.flavor === 'string' && router.query.flavor.trim()
          ? (router.query.flavor as string)
          : null;

      const patch: Partial<ReturnType<typeof useProductStore.getState>> = {};
      if (s.isOtpProduct !== false) patch.isOtpProduct = false;
      if (s.quantity !== 1) patch.quantity = 1;
      if (urlFlavor && s.flavor !== urlFlavor) patch.flavor = urlFlavor;

      if (Object.keys(patch).length) {
        useProductStore.setState(patch, false);
      }
    }

    lastGuardRef.current = guardKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, identityKey, isBundle, variantsReady, bundleReady, variants, store, pdpType]);
};
